import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, RefreshCw, Loader2, QrCode } from "lucide-react";

export default function MyQRPage() {
  const [token, setToken] = useState(null);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, q] = await Promise.all([api.get("/me/social"), api.get("/me/qr-token")]);
        setProfile(p.data);
        setToken(q.data.token);
      } catch (e) {
        toast.error("Could not load your QR");
      }
    })();
  }, []);

  const rotate = async () => {
    if (!confirm("Generate a fresh QR? Your current QR will stop working immediately.")) return;
    setBusy(true);
    try {
      const r = await api.post("/me/qr-token/regenerate");
      setToken(r.data.token);
      toast.success("New QR generated");
    } catch (e) {
      toast.error("Could not regenerate");
    } finally {
      setBusy(false);
    }
  };

  // Encode as a custom-scheme URL so other apps' camera scanners can interpret
  // it as an unconfirmed deep link. The actual app uses just the token after the `=`.
  const payload = token ? `prepmasters://friend-add?t=${token}` : "";

  return (
    <div className="max-w-md mx-auto space-y-6" data-testid="my-qr-page">
      <Link to="/student/friends" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Back to Friends
      </Link>

      <div className="text-center">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 inline-flex items-center gap-1.5">
          <QrCode className="w-3.5 h-3.5" /> My QR Code
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
          {profile?.display_handle || "—"}
        </h1>
        <p className="text-slate-600 text-sm mt-1">Have a friend scan this with their PrepMasters app.</p>
      </div>

      <Card className="p-8 border-slate-200 bg-white">
        {token ? (
          <div className="flex flex-col items-center" data-testid="my-qr-image">
            <div className="p-4 bg-white rounded-md border border-slate-200">
              <QRCodeSVG value={payload} size={240} level="H" includeMargin={false} />
            </div>
            <code className="mt-4 text-[11px] text-slate-400 tabular-nums break-all max-w-full">{token.slice(0, 24)}…</code>
          </div>
        ) : (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        )}
        <Button
          variant="outline"
          className="w-full mt-6"
          onClick={rotate}
          disabled={busy || !token}
          data-testid="my-qr-rotate-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Generate a fresh QR
        </Button>
      </Card>

      <p className="text-center text-[11px] text-slate-500">
        This QR encodes a one-way token only. It contains no email, phone, or personal info.
      </p>
    </div>
  );
}
